import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useSubscription } from '../../hooks/useSubscription';
import { CheckCircle, Crown, Users, BarChart3, HeadphonesIcon } from 'lucide-react';

const PlanSelection = ({ onPlanSelect, selectedPlan }) => {
  const { subscriptionTiers, loading } = useSubscription();
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Fallback tiers for signup flow when user isn't authenticated
  const fallbackTiers = [
    {
      id: 'free-tier',
      name: 'free',
      display_name: 'Free',
      description: 'Perfect for personal use and small projects',
      price_monthly: 0,
      price_annual: 0,
      max_project_groups: 2,
      max_projects_per_group: 3,
      max_tasks_per_project: 25,
      features: {
        priority_support: false,
        advanced_analytics: false,
        team_collaboration: false
      }
    },
    {
      id: 'pro-tier',
      name: 'pro',
      display_name: 'Pro',
      description: 'Ideal for professionals and growing teams',
      price_monthly: 5.99,
      price_annual: 59.99,
      max_project_groups: -1,
      max_projects_per_group: -1,
      max_tasks_per_project: -1,
      features: {
        priority_support: true,
        advanced_analytics: true,
        team_collaboration: true
      }
    }
  ];

  // Use fallback tiers if subscriptionTiers is empty or still loading
  const tiersToShow = subscriptionTiers && subscriptionTiers.length > 0 ? subscriptionTiers : fallbackTiers;

  const getFeatureIcon = (featureName) => {
    switch (featureName) {
      case 'priority_support':
        return <HeadphonesIcon className="h-4 w-4 text-blue-500" />;
      case 'advanced_analytics':
        return <BarChart3 className="h-4 w-4 text-purple-500" />;
      case 'team_collaboration':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatFeatureName = (featureName) => {
    return featureName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading && (!subscriptionTiers || subscriptionTiers.length === 0)) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">Select the plan that best fits your needs</p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <Button
            variant={billingCycle === 'monthly' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </Button>
          <Button
            variant={billingCycle === 'annual' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <Badge variant="secondary" className="ml-2">
              Save 17%
            </Badge>
          </Button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {tiersToShow.map((tier) => (
          <Card
            key={tier.id}
            className={`cursor-pointer transition-all relative ${
              selectedPlan?.id === tier.id
                ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg'
                : 'hover:border-gray-300 hover:shadow-md'
            } ${tier.name === 'pro' ? 'border-2 border-blue-500 shadow-lg' : 'border border-gray-200'}`}
            onClick={() => onPlanSelect(tier, billingCycle)}
          >
            {/* Popular Badge for Pro */}
            {tier.name === 'pro' && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-blue-500 text-white px-4 py-1 text-sm font-medium">
                  Most Popular
                </Badge>
              </div>
            )}
            
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center mb-3">
                {tier.name === 'pro' && <Crown className="h-8 w-8 text-blue-500 mr-2" />}
                <h3 className="text-2xl font-bold text-gray-900">{tier.display_name}</h3>
              </div>
              
              <div className="mb-4">
                <div className="text-5xl font-bold text-gray-900 mb-2">
                  ${billingCycle === 'monthly' ? tier.price_monthly : tier.price_annual}
                  <span className="text-lg font-normal text-gray-500">
                    /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </div>
                {billingCycle === 'annual' && tier.price_monthly > 0 && (
                  <div className="text-sm text-gray-500">
                    ${tier.price_monthly}/month billed annually
                  </div>
                )}
              </div>
              
              <p className="text-gray-600 text-base">{tier.description}</p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Usage Limits */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">What's included:</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">
                      {tier.max_project_groups === -1 ? 'Unlimited' : tier.max_project_groups} project groups
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">
                      {tier.max_projects_per_group === -1 ? 'Unlimited' : tier.max_projects_per_group} projects per group
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">
                      {tier.max_tasks_per_project === -1 ? 'Unlimited' : tier.max_tasks_per_project} tasks per project
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Features:</h4>
                <div className="space-y-2">
                  {Object.entries(tier.features || {}).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center space-x-3">
                      {enabled ? (
                        getFeatureIcon(feature)
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                      )}
                      <span className={`text-gray-700 ${enabled ? '' : 'text-gray-400'}`}>
                        {formatFeatureName(feature)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Select Button */}
              <div className="pt-4">
                {selectedPlan?.id === tier.id ? (
                  <Button
                    className="w-full h-12 text-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => onPlanSelect(tier, billingCycle)}
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Selected
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-12 text-lg font-medium border-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50"
                    onClick={() => onPlanSelect(tier, billingCycle)}
                  >
                    {tier.name === 'free' ? 'Start Free' : 'Get Started'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PlanSelection;
